import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateMessageTypeMutation, useUpdateMessageTypeMutation } from 'api/MessageTypeAPI';
import { AxiosResponse } from 'axios';
import { MessageMedium } from 'constants/messageMediums';

import { selectFeatureFlags } from 'core/userContext';
import { createErrorText, getDefaultSubscriptionPolicy } from 'modules/channels/utils';
import { SubscriptionPolicy } from 'modules/graphql';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { FC, ReactNode, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ApiError } from 'services/api';
import { MODAL_SIZES, MODAL_VARIANTS, Modal } from 'ui/modal';
import { IS_VALID_URL } from 'utils/validationRegex';

import { useChannelsContext } from '../../../contexts/ChannelsContext';
import { ModalProps, ModalView } from './types';
import { MESSAGE_TYPE_TEXT } from './constants';
import { ManageMessageType } from './ManageMessageType';
import { ManageDoubleOptInType } from './ManageDoubleOptIn';

const BaseSchema = z.object({
  messageTypeName: z.string().trim().min(1, { message: MESSAGE_TYPE_TEXT.INPUT_ERROR_TEXT }),
});

export const MessageTypeSchema = z.discriminatedUnion('subscriptionPolicy', [
  BaseSchema.extend({
    subscriptionPolicy: z.literal(SubscriptionPolicy.OptOut),
  }),
  BaseSchema.extend({
    subscriptionPolicy: z.literal(SubscriptionPolicy.OptIn),
  }),
  BaseSchema.extend({
    subscriptionPolicy: z.literal(SubscriptionPolicy.DoubleOptIn),
    brandName: z.string().trim().min(1, 'Brand name is required.'),
    messagingInitiative: z.string().trim().min(1, 'Messaging initiative is required.'),
    smsDisclaimerLink: z
      .string()
      .trim()
      .min(1, 'SMS disclaimer link is required.')
      .regex(IS_VALID_URL, 'Must be a valid URL with https://'),
    sender: z.number({ required_error: 'Sender is required' }),
  }),
]);

export const withFormProvider = (Component: FC<ModalProps>) => (props: ModalProps) => {
  const { MessageTypeSubscription, SMSDoubleOptIn } = useSelector(selectFeatureFlags);
  const isSmsChannel = props.channel?.messageMedium === MessageMedium.SMS;
  const defaultSubscriptionPolicy = getDefaultSubscriptionPolicy(
    MessageTypeSubscription,
    isSmsChannel && SMSDoubleOptIn,
  );
  const methods = useForm({
    defaultValues: {
      messageTypeName: props.messageType?.name || '',
      subscriptionPolicy: props.messageType?.subscriptionPolicy || defaultSubscriptionPolicy,
      brandName: props?.messageType?.subscriptionPolicyMetadata?.brandName || '',
      messagingInitiative:
        props?.messageType?.subscriptionPolicyMetadata?.messagingInitiative?.[0] || '',
      smsDisclaimerLink: props?.messageType?.subscriptionPolicyMetadata?.disclaimerLink || '',
      sender: props?.messageType?.subscriptionPolicyMetadata?.smsSenderId || undefined,
    },
    mode: 'onSubmit',
    resolver: zodResolver(MessageTypeSchema),
    reValidateMode: 'onChange',
  });

  return (
    <FormProvider {...methods}>
      <Component {...props} />
    </FormProvider>
  );
};

export const ManageMessageTypeModal = withFormProvider(
  ({ onClose, channel, messageType, isEditMode }: ModalProps) => {
    const { SMSDoubleOptIn } = useSelector(selectFeatureFlags);

    const dispatch = useDispatch();
    const { refetchMessageTypes } = useChannelsContext();
    const [modalView, setModalView] = useState<ModalView>(ModalView.CREATE_OR_UPDATE_MESSAGE_TYPE);
    const isSmsDoubleOptInView =
      SMSDoubleOptIn &&
      (modalView === ModalView.CREATE_DOUBLE_OPT_IN_MESSAGE_TYPE ||
        (messageType?.subscriptionPolicy === SubscriptionPolicy.DoubleOptIn && isEditMode));

    const {
      getValues,
      formState: { isValid },
      handleSubmit,
      watch,
    } = useFormContext();
    const subscriptionPolicyFormVal = watch('subscriptionPolicy');
    const isDoubleOptInType = subscriptionPolicyFormVal === SubscriptionPolicy.DoubleOptIn;

    const handleSuccess = (message: ReactNode) => {
      const messageTypeName = getValues('messageTypeName');
      dispatch(
        addPositiveToast(
          <>
            {message} <strong>{messageTypeName}</strong>
          </>,
        ),
      );
      refetchMessageTypes();
      onClose();
    };

    const handleError = (e: unknown, message: string) => {
      const { response } = e as ApiError;
      const errorText = createErrorText(message, response as AxiosResponse);
      dispatch(addNegativeToastWithErrorId(errorText, 'ManageMessageType', e));
      onClose();
    };

    const { isLoading: createLoading, mutate: createMessageTypeMutation } =
      useCreateMessageTypeMutation({
        onSuccess: () => handleSuccess(MESSAGE_TYPE_TEXT.CREATE_SUCCESS_MESSAGE),
        onError: (e: unknown) => handleError(e, MESSAGE_TYPE_TEXT.CREATE_ERROR_MESSAGE),
      });

    const { isLoading: updateLoading, mutate: updateMessageTypeMutation } =
      useUpdateMessageTypeMutation({
        onSuccess: () => handleSuccess(MESSAGE_TYPE_TEXT.UPDATE_SUCCESS_MESSAGE),
        onError: (e: unknown) => handleError(e, MESSAGE_TYPE_TEXT.UPDATE_ERROR_MESSAGE),
      });

    const onSubmit: SubmitHandler<FieldValues> = ({
      messageTypeName,
      subscriptionPolicy,
      brandName,
      messagingInitiative,
      smsDisclaimerLink,
      sender,
    }) => {
      if (!isValid) return null;

      const name = messageTypeName.trim();
      const doiFields = {
        brandName,
        messagingInitiative: messagingInitiative && [messagingInitiative],
        disclaimerLink: smsDisclaimerLink,
        smsSenderId: sender,
      };

      if (isEditMode) {
        const updateMessageTypePayload = {
          id: messageType?.id,
          name,
          frequencyCap: messageType?.frequencyCap,
          rateLimitPerMin: messageType?.rateLimitPerMin,
          ...(subscriptionPolicy === SubscriptionPolicy.DoubleOptIn ? doiFields : {}),
        };
        updateMessageTypeMutation(updateMessageTypePayload);
      } else {
        const createMessageTypePayload = {
          name,
          messageChannelId: channel?.id,
          subscriptionPolicy,
          ...(subscriptionPolicy === SubscriptionPolicy.DoubleOptIn ? doiFields : {}),
        };
        createMessageTypeMutation(createMessageTypePayload);
      }
    };

    return (
      <Modal
        id="manage-message-type-modal"
        isModalOpen={true}
        onClose={onClose}
        size={isSmsDoubleOptInView ? MODAL_SIZES.LARGE : MODAL_SIZES.MEDIUM}
        variant={MODAL_VARIANTS.DEFAULT}
      >
        {isSmsDoubleOptInView ? (
          <ManageDoubleOptInType
            onClose={onClose}
            channel={channel}
            messageType={messageType}
            isEditMode={isEditMode}
            isUpdateToDoubleOptInMode={false}
            onSubmit={handleSubmit(onSubmit)}
            prevAction={() => setModalView(ModalView.CREATE_OR_UPDATE_MESSAGE_TYPE)}
            actionLoading={createLoading || updateLoading}
          />
        ) : (
          <ManageMessageType
            onClose={onClose}
            channel={channel}
            messageType={messageType}
            isEditMode={isEditMode}
            onSubmit={handleSubmit(onSubmit)}
            nextAction={
              isDoubleOptInType
                ? () => setModalView(ModalView.CREATE_DOUBLE_OPT_IN_MESSAGE_TYPE)
                : undefined
            }
            actionLoading={createLoading || updateLoading}
          />
        )}
      </Modal>
    );
  },
);
